import React, { useState } from 'react';
import { Reorder } from 'framer-motion';
import { ArrowLeftIcon } from 'lucide-react';
import type { Deliverable } from '../../types/portal';
import { usePortal } from '../../contexts/PortalContext';
import { AddDeliverable } from '../../components/portal/AddDeliverable';
import { DeliverableAdmin } from '../../components/portal/DeliverableAdmin';
import { ProjectFields } from '../../components/portal/ProjectFields';
import { projectStatus } from '../../utils/status';
import { deliverableStage } from '../../utils/deliverables';
import { ClientLink } from '../../components/portal/ClientLink';
import { PortalTeam } from '../../components/portal/PortalTeam';
import { ProjectOptions } from '../../components/portal/ProjectOptions';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD } from '../../components/portal/adminStyles';
export function ProjectAdmin() {
  const {
    activeProject,
    goPortal,
    // duplicateProject stays available in context for a future project
    // options menu — it is simply not a prominent action here.
    saveAsTemplate,
    startPreview,
    archiveProject,
    reopenProject,
    setDeliverableOrder,
    notify
  } = usePortal();
  const [editing, setEditing] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [carryingId, setCarryingId] = useState<string | null>(null);
  const project = activeProject;
  const reviewCount = project.deliverables.filter(item => deliverableStage(item.status) === 'Review').length;
  const finalCount = project.deliverables.length - reviewCount;
  const hidden = project.deliverables.filter(item => !item.visibleToClient).length;
  function saveTemplate(event: React.FormEvent) {
    event.preventDefault();
    const name = templateName.trim();
    if (!name) return;
    saveAsTemplate(name);
    setTemplateName('');
    setSavingTemplate(false);
    notify(`Template saved · ${name}`);
  }
  return <div data-id="element-701" data-mp="22jytc639kcuo">
      <button type="button" onClick={() => goPortal('projects')} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-702" data-mp="22k767xzdzi5v">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-703" data-mp="1xmp7t1izwlc4" />
        Client Projects
      </button>

      <header className="mt-8 border-b border-ruleDark pb-10" data-id="element-704" data-mp="2qbcpyo8g6ulg">
        <h1 className="font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-705" data-mp="2q9jpjqofh6lw">
          {project.client.toUpperCase()}
        </h1>
        <p className="mt-6 font-display text-xl text-paper/70" data-id="element-706" data-mp="2gcx4b0s6wdjc">{project.name}</p>
        <p className="label mt-5 text-paper/40" data-id="element-707" data-mp="zz6sf7utwkz5">
          {project.type} · {projectStatus(project)} · Updated {project.updated}
          {project.archived ? ' · Archived' : ''}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-708" data-mp="3j62enac7z1px">
          <button type="button" onClick={startPreview} className={BUTTON_SOLID} data-id="element-709" data-mp="3j47rdpdh6xio">
            Preview as Client
          </button>
          <button type="button" onClick={() => setEditing(current => !current)} className={BUTTON_GHOST} data-id="element-710" data-mp="3s6qsa3xpb02a">
            {editing ? 'Close Editor' : 'Edit Project'}
          </button>
          <button type="button" onClick={() => setSavingTemplate(current => !current)} className={BUTTON_GHOST} data-id="element-711" data-mp="30erqh0xvp31q">
            Save as Template
          </button>
          {project.archived ? <button type="button" onClick={() => {
          reopenProject(project.id);
          notify('Project reopened · back in Active Projects');
        }} className={BUTTON_GHOST} data-id="element-712" data-mp="39963sxyum3i7">
              Reopen Project
            </button> : <button type="button" onClick={() => setConfirmArchive(true)} className={BUTTON_GHOST} data-id="element-713" data-mp="2pf7teyoqswzo">
              Archive Project
            </button>}
        </div>

        <ClientLink slug={project.slug} projectId={project.id} data-id="element-714" data-mp="2zd3yp9v00xpv" />

        {confirmArchive && !project.archived && <div className="mt-8 max-w-xl border border-paper/30 px-6 py-6" data-id="element-715" data-mp="344vsrudd5lkr">
            <p className="font-display text-2xl leading-snug" data-id="element-716" data-mp="393youpbd8hgl">Archive this project?</p>
            <p className="label-sm mt-4 leading-relaxed text-paper/40" data-id="element-717" data-mp="3d495cvwsaq9p">
              It will be removed from Active Projects but its project history and data will be
              retained.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-718" data-mp="3n3hsp5x596n3">
              <button type="button" onClick={() => {
            archiveProject();
            setConfirmArchive(false);
            notify('Project archived · nothing deleted');
            goPortal('projects');
          }} className={BUTTON_SOLID} data-id="element-719" data-mp="3n0lurhlaqhyn">
                Archive Project
              </button>
              <button type="button" onClick={() => setConfirmArchive(false)} className={BUTTON_QUIET} data-id="element-720" data-mp="1gcuzqsrj4yah">
                Cancel
              </button>
            </div>
          </div>}

        {savingTemplate && <form onSubmit={saveTemplate} className="mt-8 max-w-md" data-id="element-721" data-mp="2427iokcnskpz">
            <label className="block" data-id="element-722" data-mp="1z6ig085cenpg">
              <span className="label block text-paper/40" data-id="element-723" data-mp="1z3cq4qow3hso">Template Name</span>
              <input value={templateName} onChange={event => setTemplateName(event.target.value)} placeholder="e.g. 6-Episode Filmed Podcast" autoFocus className={FIELD} data-id="element-724" data-mp="1u49jx2x6b5j4" />
            </label>
            <div className="mt-6 flex items-center gap-6" data-id="element-725" data-mp="dr2u92whwnir">
              <button type="submit" className={BUTTON_SOLID} data-id="element-726" data-mp="31y2v0pv8hox4">
                Save Template
              </button>
              <button type="button" onClick={() => setSavingTemplate(false)} className={BUTTON_QUIET} data-id="element-727" data-mp="35zoewv66r3gt">
                Cancel
              </button>
            </div>
          </form>}

        {editing && <ProjectFields onClose={() => setEditing(false)} data-id="element-728" data-mp="2i9u0waa4r8uz" />}
      </header>

      <section aria-labelledby="deliverables-admin" className="mt-14" data-id="element-729" data-mp="2i8iue2pavn06">
        <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-ruleDark pb-4" data-id="element-730" data-mp="2n1c3f2rsxhi2">
          <h2 id="deliverables-admin" className="label m-0 text-paper/40" data-id="element-731" data-mp="29ap2r52hdhjc">
            Deliverables
          </h2>
          <p className="label-sm m-0 text-paper/35" data-id="element-732" data-mp="2e8gxe6uifhr8">
            {reviewCount} in review · {finalCount} final delivery
            {hidden > 0 ? ` · ${hidden} hidden from client` : ''}
          </p>
        </div>

        {project.deliverables.length === 0 ? <p className="border-b border-ruleDark py-10 font-display text-xl text-paper/40" data-id="element-733" data-mp="1kpalrbk8vlx8">
            No deliverables yet. Add whatever this project requires.
          </p> : <Reorder.Group as="ul" axis="y" values={project.deliverables} onReorder={(next: Deliverable[]) => setDeliverableOrder(next.map(item => item.id))}
      /* A recessed field while a row is lifted, so the empty slot reads as a gap. */ className={['m-0 list-none p-0 transition-colors duration-150 ease-out', carryingId ? 'bg-paper/[0.02]' : ''].join(' ')} data-id="element-734" data-mp="1ump2zddzseql">
            {project.deliverables.map(item => <DeliverableAdmin key={item.id} deliverable={item} onLift={setCarryingId} data-id="element-735" data-mp="28elqt58mo2h6" />)}
          </Reorder.Group>}

        <AddDeliverable data-id="element-736" data-mp="2ic9mbxe25e0e" />
      </section>

      <PortalTeam data-id="element-737" data-mp="2idax00r5vyhl" />
      <ProjectOptions data-id="element-738" data-mp="2idufpjomyqwo" />
    </div>;
}